import { SETTINGS_VIEW } from '../contexts/UIContext';
import { AssetDTO } from './persistent-result.type';
import { IApplicationException } from './exception.type';
import { NorthStarTarget } from './north-star-target.type';
import { IRecommendationNext, RecommendationStatus } from './recommendation-next.type';
import { IRoadmap } from './roadmap.type';
import { IUser } from './user.type';

export interface INode {
  label: string;
  key: string | number;
  children?: INode[];
  fullPath?: string;
  selected?: boolean;
  disabled?: boolean;
  level?: number;
  parent?: INode;
  opened?: boolean;
  category?: boolean;
}

export interface ISection {
  path: string;
  label: string;
  display: boolean;
  component: JSX.Element | undefined;
  isLink?: boolean;
  notification?: number;
  applyFilter?: boolean;
  displayNotification?: boolean;
  childrens?: ISection[];
  isFullScreen?: boolean;
  isNew?: boolean;
}

export interface Paginated<T> {
  items: T[];
  meta: IPagination;
}

// This is a type that represents a paginated list by nestjs-paginate.
export interface PaginatedNext<T> {
  data: T[];
  meta: {
    applicationCount: number;
    hostnameCount: number;
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
    sortBy: Array<[string, 'ASC' | 'DESC']>;
  };
}

export type GlobalStatus = 'idle' | 'error' | 'maintenance';
export type WorkerStatus = 'idle' | 'active' | 'error';

export interface IStatus {
  global: GlobalStatus;
  worker: WorkerStatus;
  lastRecordDate: string;
  lastApptioRecordMonth: string;
}

export interface IReferenceValue {
  key: number | string;
  name: string;
  resultKey?: string;
  disabled?: boolean;
  desc?: string;
  componentRefType?: string;
}

export const ALLOWED_TRIBES = [
  'GCOO/CFT/RRR',
  'GCOO/RBS/PBI/LUX/MKT',
  'GCOO/RBS/PBI/LUX/PIL',
  'GCOO/ARC/LUX',
  'GCOO/RBS/DCC/DEV/VDF',
  'GCOO/RBS/ASR/CSS/VDF',
  'GBTO/LUX/TSU',
  'GCOO/ISR/GBI',
];
export type ManagedByOptions = 'GBIS-GBSU' | 'GBIS-Non GBSU' | 'Non GBIS';

export type FlagType =
  | 'success'
  | 'warning'
  | 'danger'
  | 'dark'
  | 'secondary'
  | 'light'
  | 'transparent'
  | 'white'
  | 'black'
  | 'primary';

export type ApplicationResultType = 'value' | 'rule' | 'presence' | 'percentage';
export type ApplicationResultValue = 'good' | 'medium' | 'bad' | 'n/a' | 'n/d' | string | boolean | number;

export interface IApplicationClaim extends IBaseEntity {
  justification: string;
  status: string;
  application: IBaseEntity;
}

export interface ResultGroupedByName {
  name: string;
  results: IApplicationFullResult[];
}

export interface ResultGroupedByVersion {
  version: string | undefined;
  groupedResultsByName: ResultGroupedByName[];
}

export interface ResultGroupedByComponentRef {
  componentRef: IApplicationComponentRef | null; // null in case we want to display only type column (obsolescence score case)
  groupedResultsByVersion: ResultGroupedByVersion[];
}

export interface IGroupedComponentFull {
  type: string;
  componentsAggrByType: ResultGroupedByComponentRef[];
}

export interface IGroupedTotalCostComponents {
  type: string;
  componentsAggrByType: {
    componentRef: IApplicationComponentRef;
    groupedResults: IGroupedTotalCostResult[];
  }[];
}

export interface IGroupedTotalCostComponent {
  type: string;
  componentsAggrByType: {
    componentRef: IApplicationComponentRef;
    components: (ITotalCostResults | undefined)[];
  }[];
}

export interface IApplicationTab {
  appName: string;
  appId: number;
}

export interface IBaseEntity {
  id: number;
  createdDate: string;
  lastUpdate: string;
  createdBy: string;
  lastUpdateBy: string;
}

export interface IPagination {
  totalItems: number;
  itemCount: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
}

export interface IApplicationParentRef extends IBaseEntity {
  scopesRef: IApplicationScopeRef[];
  name: string;
  key: string;
  comment: string;
}

export interface IApplicationScopeRef extends IBaseEntity {
  componentsRef: IApplicationComponentRef[];
  key: string;
  name: string;
  comment: string;
  type: string | null;
}

export interface IApplicationRef extends IBaseEntity {
  comment: string;
  key: string;
  name: string;
}

export interface IApplicationConfidence extends IBaseEntity {
  value: number;
  comment: string;
}

export interface IApplicationSource extends IBaseEntity {
  name: string;
  link: string | null;
  comment: string | null;
}

export enum VersionRefStatus {
  DEPRECATED = 'deprecated',
  TO_BE_DEPRECATED = 'to-be-deprecated',
  UP_TO_DATE = 'up-to-date',
}

export enum ChartMode {
  FAVORITE = 'Favorites',
  EXCEPTION = 'Exceptions',
  RECOMMENDATION = 'Recommendations',
  ROADMAP = 'Roadmaps',
  APPLICATION = 'Applications',
  CERTIFICATION = 'Certifications',
  ERRORS = 'Errors',
}
export enum TypeMode {
  ACTUAL = 'Actual',
  PREV_YEAR = 'Prev year',
  FORECAST = 'Forecast',
}

export interface IApplicationVersionRef extends IBaseEntity {
  version: string;
  versionAlias: string | null;
  status: VersionRefStatus;
  description: string;
  parentRef: IApplicationParentRef;
  parents: IApplicationParent[];
  scopeRef: IApplicationScopeRef;
  scopes: IApplicationScope[];
  componentRef: IApplicationComponentRef;
  components: IApplicationComponent[];
}
export interface IApplicationComponent extends IBaseEntity {
  results: IApplicationResult[];
  componentRef: IApplicationComponentRef;
  versionRef: IApplicationVersionRef | undefined;
  recommendations: IRecommendationNext[];
  scope: IApplicationScope | undefined;
}

export type ComponentRefVersionStatus = 'deprecated' | 'to_be_deprecated' | 'up_to_date';

export interface IApplicationParent extends IBaseEntity {
  parentRef: IApplicationRef;
  scopes: IApplicationScope[];
}

export interface IApplicationRecord extends IBaseEntity {
  name: string;
  type: string;
  parents: IApplicationParent[];
  result: IApplicationResult;
  application: IApplication;
  applicationId: number;
}

export interface IApplication extends IBaseEntity {
  iAppliName: IApplicationResult;
  iAppliID: IApplicationResult;
  description: IApplicationResult | null;
  records: IApplicationRecord[];
  exceptions: IApplicationException[];
  roadmaps: IRoadmap[];
  tags: ITag[];
  users: IUser[];
  globalState: IApplicationResult;
}

export interface IApplicationPaginated extends IBaseEntity {
  iAppliNameId: number;
  iAppliIdid: number;
  globalStateId: number;
  descriptionId: number;
}
export interface IResultPaginated {
  application: IApplicationPaginated;
  results: IApplicationResult[];
}
export interface IApplicationsResultPaginated {
  items: IResultPaginated[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export interface IFavorite extends IBaseEntity {
  application: IApplication;
  user: IUser;
}
export interface IApplicationsPayload {
  items: IApplication[];
  meta: IPagination;
  links: {
    first: string;
    previous: string;
    next: string;
    last: string;
  };
}

export interface IBaseEntity {
  id: number;
  createdDate: string;
  lastUpdate: string;
  createdBy: string;
  lastUpdateBy: string;
}

export interface IPagination {
  totalItems: number;
  itemCount: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
}

export interface IApplicationParentRef extends IBaseEntity {
  scopesRef: IApplicationScopeRef[];
  name: string;
  key: string;
  comment: string;
}

export interface IApplicationScopeRef extends IBaseEntity {
  componentsRef: IApplicationComponentRef[];
  key: string;
  name: string;
  comment: string;
  type: string | null;
  resultKeys: string[];
}

export type ProfileType = 'Application manager' | 'CIO' | 'RC/Tribe manager' | 'Architect' | 'Other';

export interface IApplicationRef extends IBaseEntity {
  comment: string;
  key: string;
  name: string;
}

export interface IApplicationConfidence extends IBaseEntity {
  value: number;
  comment: string;
}

export interface IApplicationSource extends IBaseEntity {
  name: string;
  link: string | null;
  comment: string | null;
}

export interface IApplicationResult extends IBaseEntity {
  comment: string;
  isMainResult: boolean;
  key: string;
  type: string;
  value: string;
  name: string | null;
  confidence: IApplicationConfidence;
  sources: IApplicationSource[];
  tbs?: ITbsResult;
  chasingId: number | null;
  component?: IApplicationComponent;
  scope?: IApplicationScope;
}
export interface IInfrastructureComponent {
  type: string;
  components: { key: ResultKey; name: string; versions: Array<string> | null }[];
}

export interface IRemediationComponent {
  scopeKey: ResultKey;
  scopeName: string;
  components: { key: ResultKey; name: string; versions: Array<string> | null }[];
}

export interface IApplicationComponentRef extends IBaseEntity {
  type: string;
  name: string;
  key: string;
  comment: string;
  resultKeys: string[];
  scopeRefId: number;
}

export interface IApplicationScope extends IBaseEntity {
  results: IApplicationResult[];
  scopeRef: IApplicationRef;
  components: IApplicationComponent[];
  parent: IApplicationParent;
}

export interface IApplicationParent extends IBaseEntity {
  parentRef: IApplicationRef;
  scopes: IApplicationScope[];
  record: IApplicationRecord;
}

export interface IApplicationRecord extends IBaseEntity {
  name: string;
  type: string;
  parents: IApplicationParent[];
  result: IApplicationResult;
}

export interface IFavorite extends IBaseEntity {
  user: IUser;
}
export interface IApplicationsPayload {
  items: IApplication[];
  meta: IPagination;
  links: {
    first: string;
    previous: string;
    next: string;
    last: string;
  };
}

export interface ITag extends IBaseEntity {
  tagId: number;
  name: string;
  description: string;
  application: IApplication;
}

export interface IParentRefCreate {
  key: string;
  name: string;
  comment?: string;
}

export interface IComponentRefCreate {
  key: string;
  name: string;
  type?: string;
  comment?: string;
  scopeRefID: number;
}

export interface IScopeRefCreate {
  key: string;
  name: string;
  type?: string;
  comment?: string;
  parentRefID: number;
}

export type DASHBOARD_VIEW =
  | 'global_view'
  | 'certification_view'
  | 'infrastructure_view'
  | 'tco_view'
  | 'health_view'
  | 'sgmarkets_view'
  | ResultKey;

export type ApplicationScope =
  | 'global_taxon'
  | 'global_sgmarkets'
  | 'other_obsolescence'
  | 'other_health'
  | 'other_users'
  | 'other_middlewares'
  | 'other_languages'
  | 'digital-capability_web'
  | 'digital-capability_api'
  | 'digital-capability_security'
  | 'digital-capability_ci-cd'
  | 'digital-capability_monitoring'
  | 'tco_cost';

export type ApplicationComponent =
  | 'global_taxon_criticality'
  | 'global_taxon_tribe'
  | 'global_taxon_trigram'
  | 'global_taxon_owned-by'
  | 'global_taxon_country'
  | 'global_taxon_application-type'
  | 'global_sgmarkets_sgm-service'
  | 'other_obsolescence_mssql'
  | 'other_obsolescence_oracle'
  | 'other_obsolescence_physical-server'
  | 'other_obsolescence_postgresql'
  | 'other_middlewares_web-middleware'
  | 'other_middlewares_messaging-middleware'
  | 'other_middlewares_tibco-rdv-middleware'
  | 'other_middlewares_autosys-middleware'
  | 'other_languages_jvm-language'
  | 'other_languages_dot-net-language'
  | 'other_languages_c-cpp-language'
  | 'other_languages_python-language'
  | 'other_languages_datascience-language'
  | 'other_languages_web-language'
  | 'digital-capability_web_sgwt'
  | 'digital-capability_api_sgm-api'
  | 'digital-capability_security_sg-connect'
  | 'digital-capability_security_sg-iam'
  | 'digital-capability_ci-cd_github'
  | 'digital-capability_ci-cd_jenkins'
  | 'digital-capability_ci-cd_teamcity'
  | 'digital-capability_ci-cd_nexus'
  | 'digital-capability_ci-cd_artifactory-cdp'
  | 'digital-capability_ci-cd_artifactory-dsp'
  | 'digital-capability_ci-cd_sonar'
  | 'digital-capability_ci-cd_xldeploy'
  | 'digital-capability_monitoring_alerting-alerting'
  | 'digital-capability_monitoring_alerting-multi-channel-alerting'
  | 'digital-capability_monitoring_api-api-monitoring'
  | 'digital-capability_monitoring_app-apm'
  | 'digital-capability_monitoring_app-elasticsearch'
  | 'digital-capability_monitoring_app-elastic-agent-aas'
  | 'digital-capability_monitoring_app-grafana-aas'
  | 'digital-capability_monitoring_app-heartbeat-aas'
  | 'digital-capability_monitoring_app-logstash'
  | 'digital-capability_monitoring_app-metricbeat-aas'
  | 'digital-capability_monitoring_app-open-telemetry-aas'
  | 'digital-capability_monitoring_security-secure-log-platform'
  | 'digital-capability_monitoring_security-vault'
  | 'digital-capability_monitoring_web-web-monitoring'
  | 'tco_cost_gts'
  | 'tco_cost_azure'
  | 'tco_cost_workforce'
  | 'tco_cost_non-workforce'
  | 'tco_cost_software';

export interface ITbsResult {
  [key: string]: { [key: string]: number };
}

export const PARENT_RESULT_KEYS = ['cost', 'cost-prev-year', 'cost-forecast'] as const;

export const SCOPE_RESULT_KEYS = [
  'pops-users',
  'digital-capability-api',
  'digital-capability-security',
  'digital-capability-web',
  'sgmarkets',
  'obsolescence',
  'tbs',
  'finops',
  'digital-capability-monitoring',
  'health',
  'health-changes',
  'max-users',
  'digital-capability-ci-cd',
  'gbsu-cost',
  'gbsu-cost-forecast',
  'gbsu-cost-prev-year',
  'vendor-cost',
  'vendor-cost-forecast',
  'vendor-cost-prev-year',
  'infrastructure-cost',
  'infrastructure-cost-forecast',
  'infrastructure-cost-prev-year',
  'gbis-standard',
] as const;

export const COMPONENT_RESULT_KEYS = [
  'digital-capability_api_api-pub',
  'digital-capability_api_average-api-data-quality',
  'digital-capability_api_average-api-functional-quality',
  'digital-capability_api_average-api-qos-quality',
  'digital-capability_api_average-api-security-quality',
  'digital-capability_api_average-api-technical-quality',
  'digital-capability_api_sgm-api',
  'digital-capability_ci-cd_github',
  'digital-capability_ci-cd_jenkins',
  'digital-capability_ci-cd_teamcity',
  'digital-capability_ci-cd_nexus',
  'digital-capability_ci-cd_artifactory-cdp',
  'digital-capability_ci-cd_artifactory-dsp',
  'digital-capability_ci-cd_sonar',
  'digital-capability_ci-cd_xldeploy',
  'digital-capability_monitoring_api-monitoring',
  'digital-capability_monitoring_architecture-type',
  'digital-capability_monitoring_global-monitoring',
  'digital-capability_monitoring_web-monitoring',
  'digital-capability_security_criticality',
  'digital-capability_security_dataset',
  'digital-capability_security_sg-connect',
  'digital-capability_security_sg-iam',
  'digital-capability_security_tsa',
  'digital-capability_security_nist',
  'digital-capability_web_sg-bootstrap',
  'digital-capability_web_sgwt-widgets',
  'digital-capability_web_sgwt-widgets-account-center',
  'digital-capability_web_sgwt-widgets-help-center',
  'digital-capability_web_sgwt-widgets-mini-footer',
  'digital-capability_web_sgwt-widgets-splash-screen',
  'global_sgmarkets_sgm-service',
  'global_taxon_amer-managed',
  'global_taxon_application-type',
  'global_taxon_iappliname',
  'global_taxon_kearid',
  'global_taxon_iappliid',
  'global_taxon_application-manager-email',
  'global_taxon_irt',
  'global_taxon_bdr',
  'global_taxon_languages',
  'global_taxon_application-owner-email',
  'global_taxon_architecture-type',
  'global_taxon_country',
  'global_taxon_criticality',
  'global_taxon_functional-architecture',
  'global_taxon_managed-by',
  'global_taxon_nova-compatible',
  'global_taxon_status',
  'global_taxon_tribe',
  'global_taxon_trigram',
  'global_taxon_owned-by',
  'global_taxon_ownership-rc',
  'global_taxon_management-rc',
  'global_taxon_application-decom-date',
  'tco_finops_orchestrated-container',
  'other_transformation-path_transformation-path',
  'null_null_tbs-enhancement-duplicate',
  'null_null_tbs-project-duplicate',
  'null_null_tbs-support-duplicate',
  'other_gbis-standard_abap',
  'other_gbis-standard_abcpdf-dot-net',
  'other_gbis-standard_action-script',
  'other_gbis-standard_ada',
  'other_gbis-standard_adaptive-server-entreprise-ado-dot-net-data-provider',
  'other_gbis-standard_ado-dot-net',
  'other_gbis-standard_ado-dot-net-entity-framework',
  'other_gbis-standard_ado-dot-net-entity-framework-tools',
  'other_gbis-standard_ags-script',
  'other_gbis-standard_al',
  'other_gbis-standard_alloy',
  'other_gbis-standard_ampl',
  'other_gbis-standard_angel-script',
  'other_gbis-standard_antlr',
  'other_gbis-standard_apache-conf',
  'other_gbis-standard_apache-web-server',
  'other_gbis-standard_apex',
  'other_gbis-standard_api-blueprint',
  'other_gbis-standard_apl',
  'other_gbis-standard_app-dynamics-dot-net-agent',
  'other_gbis-standard_apple-script',
  'other_gbis-standard_arc',
  'other_gbis-standard_asl',
  'other_gbis-standard_asp',
  'other_gbis-standard_asp-dot-net',
  'other_gbis-standard_asp-dot-net-ajax-extensions',
  'other_gbis-standard_asp-dot-net-core-hosting-bundle',
  'other_gbis-standard_asp-dot-net-core-runtime',
  'other_gbis-standard_asp-dot-net-core-runtime-package-store',
  'other_gbis-standard_asp-dot-net-core-runtime-package-store-pre-releases',
  'other_gbis-standard_asp-dot-net-core-shared-framework',
  'other_gbis-standard_asp-dot-net-core-shared-framework-pre-releases',
  'other_gbis-standard_asp-dot-net-frameworkd-and-tools-for-visual-studio-2013',
  'other_gbis-standard_asp-dot-net-lang',
  'other_gbis-standard_asp-dot-net-mvc',
  'other_gbis-standard_asp-dot-net-web-frameworks-and-tools-for-visual-studio-2015',
  'other_gbis-standard_aspect-j',
  'other_gbis-standard_assembly',
  'other_gbis-standard_auto-hoy-key',
  'other_gbis-standard_auto-it',
  'other_gbis-standard_autosys-middleware',
  'other_gbis-standard_awk',
  'other_gbis-standard_basic',
  'other_gbis-standard_batchfile',
  'other_gbis-standard_bicep',
  'other_gbis-standard_bikeshed',
  'other_gbis-standard_blade',
  'other_gbis-standard_blend-for-visual-studio-sdk-for-dot-net',
  'other_gbis-standard_boo',
  'other_gbis-standard_brighscript',
  'other_gbis-standard_brighter-script',
  'other_gbis-standard_businessobjects',
  'other_gbis-standard_business-works',
  'other_gbis-standard_c',
  'other_gbis-standard_cap-n-proto',
  'other_gbis-standard_carto-css',
  'other_gbis-standard_chart-controls-for-microsoft-dot-net-framework',
  'other_gbis-standard_clarion',
  'other_gbis-standard_classic-asp',
  'other_gbis-standard_clean',
  'other_gbis-standard_clojure',
  'other_gbis-standard_cmake',
  'other_gbis-standard_cobol',
  'other_gbis-standard_code-contracts-for-dot-net',
  'other_gbis-standard_coffee-script',
  'other_gbis-standard_cold-fusion',
  'other_gbis-standard_common-lisp',
  'other_gbis-standard_component-art-ui-framework-for-dot-net',
  'other_gbis-standard_connect-express-for-unix',
  'other_gbis-standard_cool',
  'other_gbis-standard_coq',
  'other_gbis-standard_cpp',
  'other_gbis-standard_cruise-control-dot-net',
  'other_gbis-standard_crystal-reports-for-dot-net-framework',
  'other_gbis-standard_crystal-reports-runtime-engine-for-dot-net-framework',
  'other_gbis-standard_c-sharp',
  'other_gbis-standard_csoud-document',
  'other_gbis-standard_csound-score',
  'other_gbis-standard_css',
  'other_gbis-standard_cuda',
  'other_gbis-standard_cython',
  'other_gbis-standard_d',
  'other_gbis-standard_dart',
  'other_gbis-standard_data-provider-for-dot-net',
  'other_gbis-standard_developer-express-dot-net',
  'other_gbis-standard_digital-command-language',
  'other_gbis-standard_dockerfile',
  'other_gbis-standard_documentum',
  'other_gbis-standard_dot-net-compact-framework',
  'other_gbis-standard_dot-net-core-host-pre-releases',
  'other_gbis-standard_dot-net-core-runtime',
  'other_gbis-standard_dot-net-core-runtime-pre-release',
  'other_gbis-standard_dot-net-core-runtime-pre-releases',
  'other_gbis-standard_dot-net-core-tools-visual-studio',
  'other_gbis-standard_dot-net-core-windows-server-hosting',
  'other_gbis-standard_dot-net-framework',
  'other_gbis-standard_dot-net-framework-client-profile',
  'other_gbis-standard_dot-net-framework-extended',
  'other_gbis-standard_dot-net-framework-sdk',
  'other_gbis-standard_dot-net-memory-profiler',
  'other_gbis-standard_dot-net-reflector',
  'other_gbis-standard_dot-net-runtime',
  'other_gbis-standard_dot-net-sdk',
  'other_gbis-standard_dot-net-sdk-pre-releases',
  'other_gbis-standard_dtrace',
  'other_gbis-standard_eagle',
  'other_gbis-standard_ec',
  'other_gbis-standard_ecl',
  'other_gbis-standard_eiffel',
  'other_gbis-standard_ejs',
  'other_gbis-standard_elixir',
  'other_gbis-standard_elm',
  'other_gbis-standard_emacs-lisp',
  'other_gbis-standard_ems',
  'other_gbis-standard_eq',
  'other_gbis-standard_erlang',
  'other_gbis-standard_euphoria',
  'other_gbis-standard_extrem-application-platform-xap',
  'other_gbis-standard_faust',
  'other_gbis-standard_fluent',
  'other_gbis-standard_flux',
  'other_gbis-standard_forth',
  'other_gbis-standard_fortran',
  'other_gbis-standard_free-basic',
  'other_gbis-standard_free-marker',
  'other_gbis-standard_f-sharp',
  'other_gbis-standard_f-star',
  'other_gbis-standard_futhark',
  'other_gbis-standard_game-maker-language',
  'other_gbis-standard_gap',
  'other_gbis-standard_gcc-machine-description',
  'other_gbis-standard_gdb',
  'other_gbis-standard_gemfire',
  'other_gbis-standard_genero-forms',
  'other_gbis-standard_genshi',
  'other_gbis-standard_gherkin',
  'other_gbis-standard_giga-spaces-xap-dot-net-12-2-1',
  'other_gbis-standard_giga-spaces-xap-dot-net-16-1-0',
  'other_gbis-standard_giga-spaces-xap-dot-net-premium-edition-9-7-2-x86',
  'other_gbis-standard_glsl',
  'other_gbis-standard_gnuplot',
  'other_gbis-standard_go',
  'other_gbis-standard_grammatical-framework',
  'other_gbis-standard_groovy',
  'other_gbis-standard_hack',
  'other_gbis-standard_handlebars',
  'other_gbis-standard_harbour',
  'other_gbis-standard_haskell',
  'other_gbis-standard_hcl',
  'other_gbis-standard_hd-insight-azure',
  'other_gbis-standard_hive-ql',
  'other_gbis-standard_hlsl',
  'other_gbis-standard_html',
  'other_gbis-standard_idl',
  'other_gbis-standard_igor-pro',
  'other_gbis-standard_inno-setup',
  'other_gbis-standard_io',
  'other_gbis-standard_iprocess',
  'other_gbis-standard_iron-python-for-dot-net-4',
  'other_gbis-standard_jasmin',
  'other_gbis-standard_java',
  'other_gbis-standard_java-development-kit',
  'other_gbis-standard_java-platform',
  'other_gbis-standard_java-runtime-environement',
  'other_gbis-standard_javascript',
  'other_gbis-standard_jboss-eap',
  'other_gbis-standard_jboss-wildfly',
  'other_gbis-standard_jinja',
  'other_gbis-standard_jolie',
  'other_gbis-standard_jq',
  'other_gbis-standard_jsonnet',
  'other_gbis-standard_jupyter-notebook',
  'other_gbis-standard_kotlin',
  'other_gbis-standard_kubernetes-cloud-azure',
  'other_gbis-standard_lasso',
  'other_gbis-standard_less',
  'other_gbis-standard_lex',
  'other_gbis-standard_limbo',
  'other_gbis-standard_linux',
  'other_gbis-standard_liquid',
  'other_gbis-standard_llvm',
  'other_gbis-standard_logos',
  'other_gbis-standard_lua',
  'other_gbis-standard_m',
  'other_gbis-standard_m4',
  'other_gbis-standard_makefile',
  'other_gbis-standard_mako',
  'other_gbis-standard_mask',
  'other_gbis-standard_mathematica',
  'other_gbis-standard_matlab',
  'other_gbis-standard_max',
  'other_gbis-standard_mercury',
  'other_gbis-standard_meson',
  'other_gbis-standard_messaging-middleware',
  'other_gbis-standard_mirc-script',
  'other_gbis-standard_modelica',
  'other_gbis-standard_module-management-system',
  'other_gbis-standard_moocode',
  'other_gbis-standard_moon-script',
  'other_gbis-standard_mq',
  'other_gbis-standard_mssql',
  'other_gbis-standard_mustache',
  'other_gbis-standard_myghty',
  'other_gbis-standard_nasl',
  'other_gbis-standard_ncl',
  'other_gbis-standard_new-lisp',
  'other_gbis-standard_nextflow',
  'other_gbis-standard_nginx',
  'other_gbis-standard_nix',
  'other_gbis-standard_nsis',
  'other_gbis-standard_nu',
  'other_gbis-standard_objective-c',
  'other_gbis-standard_objective-cpp',
  'other_gbis-standard_objective-j',
  'other_gbis-standard_ocaml',
  'other_gbis-standard_opa',
  'other_gbis-standard_open-edge-abl',
  'other_gbis-standard_open-java-development-kit',
  'other_gbis-standard_open-jdk-runtime-environement',
  'other_gbis-standard_open-policy-agent',
  'other_gbis-standard_oracle',
  'other_gbis-standard_part-cover-dot-net',
  'other_gbis-standard_pascal',
  'other_gbis-standard_pawn',
  'other_gbis-standard_peoplesoft',
  'other_gbis-standard_perl',
  'other_gbis-standard_perl-6',
  'other_gbis-standard_pgsql-azure',
  'other_gbis-standard_php',
  'other_gbis-standard_physical-server',
  'other_gbis-standard_pig-latin',
  'other_gbis-standard_pl-pgsql',
  'other_gbis-standard_pl-sql',
  'other_gbis-standard_postgresql',
  'other_gbis-standard_post-script',
  'other_gbis-standard_pov-ray-sdl',
  'other_gbis-standard_power-bi-report-server',
  'other_gbis-standard_power-builder',
  'other_gbis-standard_powercenter',
  'other_gbis-standard_power-shell',
  'other_gbis-standard_prolog',
  'other_gbis-standard_protocol-buffer',
  'other_gbis-standard_pug',
  'other_gbis-standard_puppet',
  'other_gbis-standard_pure-basic',
  'other_gbis-standard_pure-data',
  'other_gbis-standard_python',
  'other_gbis-standard_q',
  'other_gbis-standard_qmake',
  'other_gbis-standard_qml',
  'other_gbis-standard_qt-script',
  'other_gbis-standard_r',
  'other_gbis-standard_rabbit-mq',
  'other_gbis-standard_raku',
  'other_gbis-standard_rebol',
  'other_gbis-standard_redcode',
  'other_gbis-standard_rescript',
  'other_gbis-standard_rexx',
  'other_gbis-standard_rich-text-format',
  'other_gbis-standard_robot-framework',
  'other_gbis-standard_roff',
  'other_gbis-standard_rpc',
  'other_gbis-standard_ruby',
  'other_gbis-standard_rust',
  'other_gbis-standard_sas',
  'other_gbis-standard_sass',
  'other_gbis-standard_scala',
  'other_gbis-standard_scheme',
  'other_gbis-standard_scilab',
  'other_gbis-standard_scss',
  'other_gbis-standard_sed',
  'other_gbis-standard_server-gen',
  'other_transformation-path_server-gen-one',
  'other_transformation-path_server-gen-two',
  'other_transformation-path_server-tradi',
  'other_transformation-path_server-azure',
  'other_transformation-path_server-aws',
  'other_gbis-standard_shell',
  'other_gbis-standard_slice',
  'other_gbis-standard_smalltalk',
  'other_gbis-standard_smarty',
  'other_gbis-standard_smpl',
  'other_gbis-standard_source-pawn',
  'other_gbis-standard_sonar',
  'other_gbis-standard_sql',
  'other_gbis-standard_sqlpl',
  'other_gbis-standard_sql-server-analysis-services',
  'other_gbis-standard_sql-server-integration-services',
  'other_gbis-standard_sql-server-reporting-services',
  'other_gbis-standard_srecode-template',
  'other_gbis-standard_standard-ml',
  'other_gbis-standard_starlark',
  'other_gbis-standard_stata',
  'other_gbis-standard_sterling-file-gateway',
  'other_gbis-standard_string-template',
  'other_gbis-standard_stylus',
  'other_gbis-standard_svelte',
  'other_gbis-standard_swift',
  'other_gbis-standard_swig',
  'other_gbis-standard_synchronization-services-for-ado-dot-net',
  'other_gbis-standard_system-verilog',
  'other_gbis-standard_talend-bigdata',
  'other_gbis-standard_tcl',
  'other_gbis-standard_tex',
  'other_gbis-standard_thrift',
  'other_gbis-standard_tibco-rdv-middleware',
  'other_gbis-standard_tla',
  'other_gbis-standard_tomcat',
  'other_gbis-standard_tsa',
  'other_gbis-standard_tsql',
  'other_gbis-standard_tuxedo-dot-net-client',
  'other_gbis-standard_twig',
  'other_gbis-standard_typescript',
  'other_gbis-standard_ui-for-asp-dot-net-mvc',
  'other_gbis-standard_vba',
  'other_gbis-standard_vb-script',
  'other_gbis-standard_vim-script',
  'other_gbis-standard_vim-snippet',
  'other_gbis-standard_vision-for-dot-net',
  'other_gbis-standard_visual-basic',
  'other_gbis-standard_visual-basic-6',
  'other_gbis-standard_visual-basic-dot-net',
  'other_gbis-standard_visual-studio-ado-dot-net-entity-framework-tools',
  'other_gbis-standard_vue',
  'other_gbis-standard_web-assembly',
  'other_gbis-standard_weblogic-server',
  'other_gbis-standard_webmethods',
  'other_gbis-standard_web-middleware',
  'other_gbis-standard_windows',
  'other_gbis-standard_windows-azure-libraries-for-dot-net',
  'other_gbis-standard_xap-dot-net',
  'other_gbis-standard_x-base',
  'other_gbis-standard_x-proc',
  'other_gbis-standard_x-query',
  'other_gbis-standard_xs',
  'other_gbis-standard_xslt',
  'other_gbis-standard_xtend',
  'other_gbis-standard_yacc',
  'other_gbis-standard_zen-script',
  'other_gbis-standard_xonsh',
  'other_gbis-standard_filebench-wml',
  'other_gbis-standard_reason',
  'other_gbis-standard_slash',
  'other_gbis-standard_v',
  'other_gbis-standard_verilog',
  'other_health_changes',
  'other_health_emergency-changes',
  'other_health_incidents',
  'other_health_mim',
  'other_health_sev-0-incidents',
  'other_health_sev-1-incidents',
  'other_health_sev-2-incidents',
  'other_middlewares_autosys-middleware',
  'other_middlewares_messaging-middleware',
  'other_middlewares_tibco-rdv-middleware',
  'other_middlewares_web-middleware',
  'other_obsolescence_abap',
  'other_obsolescence_abcpdf-dot-net',
  'other_obsolescence_action-script',
  'other_obsolescence_ada',
  'other_obsolescence_adaptive-server-entreprise-ado-dot-net-data-provider',
  'other_obsolescence_ado-dot-net',
  'other_obsolescence_ado-dot-net-entity-framework',
  'other_obsolescence_ado-dot-net-entity-framework-tools',
  'other_obsolescence_ags-script',
  'other_obsolescence_al',
  'other_obsolescence_alloy',
  'other_obsolescence_ampl',
  'other_obsolescence_angel-script',
  'other_obsolescence_antlr',
  'other_obsolescence_apache-conf',
  'other_obsolescence_apache-web-server',
  'other_obsolescence_apex',
  'other_obsolescence_api-blueprint',
  'other_obsolescence_apl',
  'other_obsolescence_app-dynamics-dot-net-agent',
  'other_obsolescence_apple-script',
  'other_obsolescence_arc',
  'other_obsolescence_asl',
  'other_obsolescence_asp',
  'other_obsolescence_asp-dot-net',
  'other_obsolescence_asp-dot-net-ajax-extensions',
  'other_obsolescence_asp-dot-net-core-hosting-bundle',
  'other_obsolescence_asp-dot-net-core-runtime',
  'other_obsolescence_asp-dot-net-core-runtime-package-store',
  'other_obsolescence_asp-dot-net-core-runtime-package-store-pre-releases',
  'other_obsolescence_asp-dot-net-core-shared-framework',
  'other_obsolescence_asp-dot-net-core-shared-framework-pre-releases',
  'other_obsolescence_asp-dot-net-frameworkd-and-tools-for-visual-studio-2013',
  'other_obsolescence_asp-dot-net-lang',
  'other_obsolescence_asp-dot-net-mvc',
  'other_obsolescence_asp-dot-net-web-frameworks-and-tools-for-visual-studio-2015',
  'other_obsolescence_aspect-j',
  'other_obsolescence_assembly',
  'other_obsolescence_auto-hoy-key',
  'other_obsolescence_auto-it',
  'other_obsolescence_autosys-middleware',
  'other_obsolescence_awk',
  'other_obsolescence_basic',
  'other_obsolescence_batchfile',
  'other_obsolescence_bicep',
  'other_obsolescence_bikeshed',
  'other_obsolescence_blade',
  'other_obsolescence_blend-for-visual-studio-sdk-for-dot-net',
  'other_obsolescence_boo',
  'other_obsolescence_brighscript',
  'other_obsolescence_brighter-script',
  'other_obsolescence_businessobjects',
  'other_obsolescence_business-works',
  'other_obsolescence_c',
  'other_obsolescence_cap-n-proto',
  'other_obsolescence_carto-css',
  'other_obsolescence_chart-controls-for-microsoft-dot-net-framework',
  'other_obsolescence_clarion',
  'other_obsolescence_classic-asp',
  'other_obsolescence_clean',
  'other_obsolescence_clojure',
  'other_obsolescence_cmake',
  'other_obsolescence_cobol',
  'other_obsolescence_code-contracts-for-dot-net',
  'other_obsolescence_coffee-script',
  'other_obsolescence_cold-fusion',
  'other_obsolescence_common-lisp',
  'other_obsolescence_component-art-ui-framework-for-dot-net',
  'other_obsolescence_connect-express-for-unix',
  'other_obsolescence_cool',
  'other_obsolescence_coq',
  'other_obsolescence_cpp',
  'other_obsolescence_cruise-control-dot-net',
  'other_obsolescence_crystal-reports-for-dot-net-framework',
  'other_obsolescence_crystal-reports-runtime-engine-for-dot-net-framework',
  'other_obsolescence_c-sharp',
  'other_obsolescence_csoud-document',
  'other_obsolescence_csound-score',
  'other_obsolescence_css',
  'other_obsolescence_cuda',
  'other_obsolescence_cython',
  'other_obsolescence_d',
  'other_obsolescence_dart',
  'other_obsolescence_data-provider-for-dot-net',
  'other_obsolescence_developer-express-dot-net',
  'other_obsolescence_digital-command-language',
  'other_obsolescence_dockerfile',
  'other_obsolescence_documentum',
  'other_obsolescence_dot-net-compact-framework',
  'other_obsolescence_dot-net-core-host-pre-releases',
  'other_obsolescence_dot-net-core-runtime',
  'other_obsolescence_dot-net-core-runtime-pre-release',
  'other_obsolescence_dot-net-core-runtime-pre-releases',
  'other_obsolescence_dot-net-core-tools-visual-studio',
  'other_obsolescence_dot-net-core-windows-server-hosting',
  'other_obsolescence_dot-net-framework',
  'other_obsolescence_dot-net-framework-client-profile',
  'other_obsolescence_dot-net-framework-extended',
  'other_obsolescence_dot-net-framework-sdk',
  'other_obsolescence_dot-net-memory-profiler',
  'other_obsolescence_dot-net-reflector',
  'other_obsolescence_dot-net-runtime',
  'other_obsolescence_dot-net-sdk',
  'other_obsolescence_dot-net-sdk-pre-releases',
  'other_obsolescence_dtrace',
  'other_obsolescence_eagle',
  'other_obsolescence_ec',
  'other_obsolescence_ecl',
  'other_obsolescence_eiffel',
  'other_obsolescence_ejs',
  'other_obsolescence_elixir',
  'other_obsolescence_elm',
  'other_obsolescence_emacs-lisp',
  'other_obsolescence_ems',
  'other_obsolescence_eq',
  'other_obsolescence_erlang',
  'other_obsolescence_euphoria',
  'other_obsolescence_extrem-application-platform-xap',
  'other_obsolescence_faust',
  'other_obsolescence_filebench-wml',
  'other_obsolescence_fluent',
  'other_obsolescence_flux',
  'other_obsolescence_forth',
  'other_obsolescence_fortran',
  'other_obsolescence_free-basic',
  'other_obsolescence_free-marker',
  'other_obsolescence_f-sharp',
  'other_obsolescence_f-star',
  'other_obsolescence_futhark',
  'other_obsolescence_game-maker-language',
  'other_obsolescence_gap',
  'other_obsolescence_gcc-machine-description',
  'other_obsolescence_gdb',
  'other_obsolescence_gemfire',
  'other_obsolescence_genero-forms',
  'other_obsolescence_genshi',
  'other_obsolescence_gherkin',
  'other_obsolescence_giga-spaces-xap-dot-net-12-2-1',
  'other_obsolescence_giga-spaces-xap-dot-net-16-1-0',
  'other_obsolescence_giga-spaces-xap-dot-net-premium-edition-9-7-2-x86',
  'other_obsolescence_glsl',
  'other_obsolescence_gnuplot',
  'other_obsolescence_go',
  'other_obsolescence_grammatical-framework',
  'other_obsolescence_groovy',
  'other_obsolescence_hack',
  'other_obsolescence_handlebars',
  'other_obsolescence_harbour',
  'other_obsolescence_haskell',
  'other_obsolescence_hcl',
  'other_obsolescence_hd-insight-azure',
  'other_obsolescence_hive-ql',
  'other_obsolescence_hlsl',
  'other_obsolescence_html',
  'other_obsolescence_idl',
  'other_obsolescence_igor-pro',
  'other_obsolescence_inno-setup',
  'other_obsolescence_io',
  'other_obsolescence_iprocess',
  'other_obsolescence_iron-python-for-dot-net-4',
  'other_obsolescence_jasmin',
  'other_obsolescence_java',
  'other_obsolescence_java-development-kit',
  'other_obsolescence_java-platform',
  'other_obsolescence_java-runtime-environement',
  'other_obsolescence_javascript',
  'other_obsolescence_jboss-eap',
  'other_obsolescence_jboss-wildfly',
  'other_obsolescence_jinja',
  'other_obsolescence_jolie',
  'other_obsolescence_jq',
  'other_obsolescence_jsonnet',
  'other_obsolescence_jupyter-notebook',
  'other_obsolescence_kotlin',
  'other_obsolescence_sonar',
  'other_obsolescence_kubernetes',
  'other_obsolescence_kubernetes-cloud-azure',
  'other_obsolescence_lasso',
  'other_obsolescence_less',
  'other_obsolescence_lex',
  'other_obsolescence_limbo',
  'other_obsolescence_linux',
  'other_obsolescence_liquid',
  'other_obsolescence_llvm',
  'other_obsolescence_logos',
  'other_obsolescence_lua',
  'other_obsolescence_m',
  'other_obsolescence_m4',
  'other_obsolescence_makefile',
  'other_obsolescence_mako',
  'other_obsolescence_mask',
  'other_obsolescence_mathematica',
  'other_obsolescence_matlab',
  'other_obsolescence_max',
  'other_obsolescence_mercury',
  'other_obsolescence_meson',
  'other_obsolescence_messaging-middleware',
  'other_obsolescence_mirc-script',
  'other_obsolescence_modelica',
  'other_obsolescence_module-management-system',
  'other_obsolescence_moocode',
  'other_obsolescence_moon-script',
  'other_obsolescence_mq',
  'other_obsolescence_mssql',
  'other_obsolescence_mustache',
  'other_obsolescence_myghty',
  'other_obsolescence_nasl',
  'other_obsolescence_ncl',
  'other_obsolescence_new-lisp',
  'other_obsolescence_nextflow',
  'other_obsolescence_nginx',
  'other_obsolescence_nix',
  'other_obsolescence_nsis',
  'other_obsolescence_nu',
  'other_obsolescence_objective-c',
  'other_obsolescence_objective-cpp',
  'other_obsolescence_objective-j',
  'other_obsolescence_ocaml',
  'other_obsolescence_opa',
  'other_obsolescence_open-edge-abl',
  'other_obsolescence_open-java-development-kit',
  'other_obsolescence_open-jdk-runtime-environement',
  'other_obsolescence_open-policy-agent',
  'other_obsolescence_oracle',
  'other_obsolescence_part-cover-dot-net',
  'other_obsolescence_pascal',
  'other_obsolescence_pawn',
  'other_obsolescence_peoplesoft',
  'other_obsolescence_perl',
  'other_obsolescence_perl-6',
  'other_obsolescence_pgsql-azure',
  'other_obsolescence_php',
  'other_obsolescence_physical-server',
  'other_obsolescence_pig-latin',
  'other_obsolescence_pl-pgsql',
  'other_obsolescence_pl-sql',
  'other_obsolescence_postgresql',
  'other_obsolescence_post-script',
  'other_obsolescence_pov-ray-sdl',
  'other_obsolescence_power-bi-report-server',
  'other_obsolescence_power-builder',
  'other_obsolescence_powercenter',
  'other_obsolescence_power-shell',
  'other_obsolescence_prolog',
  'other_obsolescence_protocol-buffer',
  'other_obsolescence_pug',
  'other_obsolescence_puppet',
  'other_obsolescence_pure-basic',
  'other_obsolescence_pure-data',
  'other_obsolescence_python',
  'other_obsolescence_q',
  'other_obsolescence_qmake',
  'other_obsolescence_qml',
  'other_obsolescence_qt-script',
  'other_obsolescence_r',
  'other_obsolescence_rabbit-mq',
  'other_obsolescence_raku',
  'other_obsolescence_reason',
  'other_obsolescence_rebol',
  'other_obsolescence_redcode',
  'other_obsolescence_rescript',
  'other_obsolescence_rexx',
  'other_obsolescence_rich-text-format',
  'other_obsolescence_robot-framework',
  'other_obsolescence_roff',
  'other_obsolescence_rpc',
  'other_obsolescence_ruby',
  'other_obsolescence_rust',
  'other_obsolescence_sas',
  'other_obsolescence_sass',
  'other_obsolescence_scala',
  'other_obsolescence_scheme',
  'other_obsolescence_scilab',
  'other_obsolescence_scss',
  'other_obsolescence_sed',
  'other_obsolescence_server-gen',
  'other_obsolescence_shell',
  'other_obsolescence_slash',
  'other_obsolescence_slice',
  'other_obsolescence_smalltalk',
  'other_obsolescence_smarty',
  'other_obsolescence_smpl',
  'other_obsolescence_source-pawn',
  'other_obsolescence_sql',
  'other_obsolescence_sqlpl',
  'other_obsolescence_sql-server-analysis-services',
  'other_obsolescence_sql-server-integration-services',
  'other_obsolescence_sql-server-reporting-services',
  'other_obsolescence_srecode-template',
  'other_obsolescence_standard-ml',
  'other_obsolescence_starlark',
  'other_obsolescence_stata',
  'other_obsolescence_sterling-file-gateway',
  'other_obsolescence_string-template',
  'other_obsolescence_stylus',
  'other_obsolescence_svelte',
  'other_obsolescence_swift',
  'other_obsolescence_swig',
  'other_obsolescence_synchronization-services-for-ado-dot-net',
  'other_obsolescence_system-verilog',
  'other_obsolescence_talend-bigdata',
  'other_obsolescence_tcl',
  'other_obsolescence_tex',
  'other_obsolescence_thrift',
  'other_obsolescence_tibco-rdv-middleware',
  'other_obsolescence_tla',
  'other_obsolescence_tomcat',
  'other_obsolescence_tsa',
  'other_obsolescence_tsql',
  'other_obsolescence_tuxedo-dot-net-client',
  'other_obsolescence_twig',
  'other_obsolescence_typescript',
  'other_obsolescence_ui-for-asp-dot-net-mvc',
  'other_obsolescence_v',
  'other_obsolescence_vba',
  'other_obsolescence_vb-script',
  'other_obsolescence_verilog',
  'other_obsolescence_vim-script',
  'other_obsolescence_vim-snippet',
  'other_obsolescence_vision-for-dot-net',
  'other_obsolescence_visual-basic',
  'other_obsolescence_visual-basic-6',
  'other_obsolescence_visual-basic-dot-net',
  'other_obsolescence_visual-studio-ado-dot-net-entity-framework-tools',
  'other_obsolescence_vue',
  'other_obsolescence_web-assembly',
  'other_obsolescence_weblogic-server',
  'other_obsolescence_webmethods',
  'other_obsolescence_web-middleware',
  'other_obsolescence_windows',
  'other_obsolescence_windows-azure-libraries-for-dot-net',
  'other_obsolescence_xap-dot-net',
  'other_obsolescence_x-base',
  'other_obsolescence_xonsh',
  'other_obsolescence_x-proc',
  'other_obsolescence_x-query',
  'other_obsolescence_xs',
  'other_obsolescence_xslt',
  'other_obsolescence_xtend',
  'other_obsolescence_yacc',
  'other_obsolescence_zen-script',
  'tco_gbsu-cost_non-workforce',
  'tco_gbsu-cost_non-workforce-forecast',
  'tco_gbsu-cost_non-workforce-prev-year',
  'tco_gbsu-cost_workforce',
  'tco_gbsu-cost_workforce-forecast',
  'tco_gbsu-cost_workforce-prev-year',
  'tco_infrastructure-cost_azure',
  'tco_infrastructure-cost_azure-forecast',
  'tco_infrastructure-cost_gts',
  'tco_infrastructure-cost_gts-forecast',
  'tco_infrastructure-cost_gts-prev-year',
  'tco_tbs_tbs-enhancement',
  'tco_tbs_tbs-financial',
  'tco_tbs_tbs-infrastructure',
  'tco_tbs_tbs-management',
  'tco_tbs_tbs-project',
  'tco_tbs_tbs-support',
  'tco_tbs_tbs-technical',
  'tco_tbs_tbs-enhancement-duplicate',
  'tco_tbs_tbs-financial-duplicate',
  'tco_tbs_tbs-infrastructure-duplicate',
  'tco_tbs_tbs-management-duplicate',
  'tco_tbs_tbs-project-duplicate',
  'tco_tbs_tbs-support-duplicate',
  'tco_tbs_tbs-technical-duplicate',
  'tco_vendor-cost_software',
  'tco_vendor-cost_software-forecast',
  'tco_vendor-cost_software-prev-year',
  'iAppliName',
  'languages',
] as const;

export const COST_RESULT_KEYS = [
  'tco_gbsu-cost_workforce',
  'tco_gbsu-cost_workforce-prev-year',
  'tco_gbsu-cost_workforce-forecast',
  'tco_gbsu-cost_non-workforce',
  'tco_gbsu-cost_non-workforce-forecast',
  'tco_gbsu-cost_non-workforce-prev-year',
  'tco_vendor-cost_software',
  'tco_vendor-cost_software-forecast',
  'tco_vendor-cost_software-prev-year',
  'tco_infrastructure-cost_gts',
  'tco_infrastructure-cost_gts-forecast',
  'tco_infrastructure-cost_gts-prev-year',
  'tco_infrastructure-cost_azure',
  'tco_infrastructure-cost_azure-forecast',
  'cost',
  'cost-prev-year',
  'cost-forecast',
] as const;

export const OBSOLESCENCE_PERCENTAGE_KEY = 'obsolescence-percentage';
export const OBSOLESCENCE_ASSET_SUM_KEY = 'obsolescence-assets-sum';

export const RESULT_KEYS = [
  ...COMPONENT_RESULT_KEYS,
  ...SCOPE_RESULT_KEYS,
  ...PARENT_RESULT_KEYS,
  OBSOLESCENCE_PERCENTAGE_KEY,
  OBSOLESCENCE_ASSET_SUM_KEY,
] as const;

export type ComponentResultKey = (typeof COMPONENT_RESULT_KEYS)[number];
export type ScopeResultKey = (typeof SCOPE_RESULT_KEYS)[number];
export type ParentResultKey = (typeof PARENT_RESULT_KEYS)[number];
export type ResultKey = (typeof RESULT_KEYS)[number];
export type CostResultKey = (typeof COST_RESULT_KEYS)[number];

export type IApplicationFlat = {
  applicationId: number;
  iAppliID: string;
  iAppliName: string;
  recordDate: Date;
  createdDate: Date;
  description: string;
} & { [key in ComponentResultKey | ScopeResultKey]: string };

export interface INotifications {
  appId: number;
  roadmapsOutdated: number;
  claimsRequested: number;
  recommendation: number;
}

export type NotificationType = 'claims' | 'roadmaps' | 'recommendation' | 'exception' | 'roadmapsOutdated';

export type IInformationFormat = {
  key: string;
  children: IInformationFormat[];
};

export interface ICertified {
  appId: string;
  isCertified: boolean;
}

export enum ResultStatus {
  CERTIFIED = 'certified',
  ERROR = 'error',
  IDLE = 'idle',
}

export type MetassetSonar = {
  bugs: {
    count: number;
    rating: string;
  };
  vulnerabilities: {
    count: number;
    rating: string;
  };
  hotspotsReviewed: {
    count: number;
    rating: string;
  };
  codeSmell: {
    count: number;
    rating: string;
  };
  coverage: {
    percentage: number;
    lineCoverage: number;
  };
  duplications: {
    duplicatedLines: number;
    duplicatedLinesDensity: number;
  };
  ncloc: number;
  languageList: Array<string>;
  lastAnalysisDate: string;
  assetUrl: string;
  assetId: string;
};

export type TrafficManagerMember = {
  ip: string;
  name: string;
  priority: number;
  region: string;
  availabilityZone: string;
  uuid: string;
  enabled: boolean;
};

export interface IMetadata {
  previousValueCmp?: 'decrease' | 'equal' | 'increase' | null;
  ruleVersion?: string;
  ruleLink?: string;
  ruleKey?: string;
  name?: string;
  isCost?: boolean;
  importMonth?: string;
  isDuplicatedApptioResult?: boolean;
  activeSupport?: string;
  activeSupportSource?: string;
  activeSupportLastUpdateBy?: string;
  activeSupportLastUpdateDate?: string;
  securitySupport?: string;
  securitySupportSource?: string;
  securitySupportLastUpdateBy?: string;
  securitySupportLastUpdateDate?: string;
  extendedSupport?: string;
  extendedSupportSource?: string;
  extendedSupportLastUpdateBy?: string;
  extendedSupportLastUpdateDate?: string;
  lifeCycleManagement?: string;
  lifeCycleManagementSource?: string;
  lifeCycleManagementLastUpdateBy?: string;
  lifeCycleManagementLastUpdateDate?: string;
  eolScore?: string;
  link?: string;
  host?: string; // hostname of the dali asset
  zone?: string; // zone of the dali asset
  uid?: string; // uid of the dali asset
  serverVersion?: string; // os version for transformation path assets
  serverOs?: string; // os name for transformation path assets
  members?: Array<TrafficManagerMember>;
  obsolescence?: {
    totalInstanceCount: number;
    obsoleteInstanceCount: number;
    obsolescenceDenominator: number;
    percentageObsolete: number;
  };
  serverCloudType?: string;
  daliGoldenSource?: string;
  isActive?: boolean;
  monitoring?: Record<string, unknown>[] | null;
  webAppUrls?: string;
  /**
   * Extra information about a result
   */
  sonar?: MetassetSonar;
  data?: {
    [key: string]: string;
  };
  dataArray?: {
    [key: string]: Array<Record<string, string>>;
  };
  northStarTarget?: NorthStarTarget;
  itResourceSubTower?: string;
  itResourceTower?: string;
  cve?: string; // JFrog SCA
  fixedVersion?: string; // JFrog SCA
  impactPath?: string; // JFrog SCA
  path?: string; // JFrog SCA
}

export interface IVersion {
  id: number;
  version: string;
  status: string;
  description: string | null;
}

export type IdType = 'iAppliID' | 'kearID';

export interface IApplicationFullResult {
  createdDate: Date;
  applicationId: number;
  status: ResultStatus;
  key: string;
  type: ApplicationResultType;
  resultId: number;
  name: string;
  parentRefId: number | null;
  scopeRefId: number | null;
  componentRefId: number | null;
  parentId: number | null;
  scopeId: number | null;
  componentId: number | null;
  valueMutated: string;
  commentMutated: string;
  sourceIds: number[];
  sources: Array<{ name: string; link: string | null; comment: string | null }>;
  sourceIdsMutated: number[];
  recommendationIds: number[];
  confidenceId: number;
  confidenceMutated: {
    value: number;
    comment: string;
  };
  version: Pick<IApplicationVersionRef, 'description' | 'id' | 'status' | 'version'>;
  isMainResult: boolean;
  value: string;
  comment: string;
  good?: string;
  medium?: string;
  bad?: string;
  metadata: IMetadata | null;
  rule: ScopeResult;
}

export type IApplicationFull = {
  applicationId: number;
  global_taxon_iappliid: IApplicationFullResult[];
  global_taxon_iappliname: IApplicationFullResult[];
  global_taxon_kearid: IApplicationFullResult[];
  recordDate: string;
  global_taxon_description: IApplicationFullResult[];
  ownerIds: number[];
  tagIds: number[];
  claimIds: number[];
  exceptionIds: number[];
  recommendationIdsRecorded: number[];
  roadmapIdsRecorded: number[];
  roadmapIds: number[];
} & { [key in ResultKey]: IApplicationFullResult[] } & {
  [key in keyof IBaseEntity]: IBaseEntity[key];
};

export interface IApplicationsFullPayload {
  items: IApplicationFull[];
  meta: IPagination;
  links: {
    first: string;
    previous: string;
    next: string;
    last: string;
  };
}

export interface IHeader {
  resultKey: ResultKey | string;
  label: string | null;
  children?: IHeader[];
}
export interface IApplicationsFullResult {
  recordDate: Date;
  applicationId: number;
  [index: number]: IApplicationFullResult;
}

export type PreviousValueCmpType = 'equal' | 'increase' | 'decrease';

export interface IGroupedApplications {
  applicationName: string;
  results: IApplicationFull[];
}

export interface IGroupedApplicationsPaginate {
  applicationsFull: IApplicationFull[];
  totalPages: number;
  currentPage: number;
  totalItems: number;
}

export interface IUniqueVisitors {
  linePoints: IUniqueVisitorsInformation[];
  timeBucketApplied: string;
}

export interface IUniqueVisitorsInformation {
  uniqueVisitorsNumber: number;
  intervalStartingDate: Date;
}

export interface IEntityAggrByDate {
  date: string;
  count: number;
}

export interface ICountEvolution {
  label: string;
  results: IEntityAggrByDate[];
}
export interface IKpiKeys {
  applicationsCount: number;
  favoritesCount: number;
  recommendationsCount: number;
  exceptionsCount: number;
  roadmapsCount: number;
  certificationsCount: number;
  errorsCount: number;
}
export interface ICountKpi {
  date: string;
  counts: IKpiKeys;
}

export interface IGroupedTotalCostResult {
  groupedType: string;
  children: string[];
}

export interface ITotalCostResults {
  results: Partial<IApplicationResult>[];
  label: string;
}
export interface IHighchartsEvolution {
  date: string;
  value: number | null;
}
export interface IHighchartData {
  name: string;
  key?: string;
  data: IHighchartsEvolution[];
  stack: string;
  color: string;
}

export type ITotalRecord = {
  recordDate: string;
} & { [key in CostResultKey]: string };

export interface IMenuItem<T> {
  label: string;
  view: T;
  display: boolean;
  children?: IMenuItem<T>[];
}
export type SettingMenu = Array<IMenuItem<SETTINGS_VIEW>>;
export type DashboardMenu = Array<IMenuItem<DASHBOARD_VIEW>>;

export interface IChasing extends IBaseEntity {
  applicationId: number;
  chasingRefId: number | null;
  roadmapId: number | null;
  componentStatusId: number | null;
  resultId: number | null;
  action: string;
  result: IApplicationResult | null;
  roadmap: IRoadmap;
  chasingRef?: IChasingRef;
  recommendation: IRecommendationNext;
}

export interface IChasingRef extends IBaseEntity {
  recordFrom: string;
  recordTo: string;
  resultKey: string | null;
  type: string;
  targetedResultValue: string | null;
  targetedMode: string | null;
  chasings: IChasing[];
  roadmaps: IRoadmap[];
  recommendations: IRecommendationNext[];
  applicationId: null | number;
}

export type ChasingType = 'component-status' | 'roadmap' | 'result' | 'recommendation' | 'application';

export interface IActionPayload {
  action: 'send-mail' | 'none';
  mailBody: string;
  applicationIds?: number[];
}
export interface IApplicationsLanguages {
  name: string;
  percentage: number;
  repositories?: Array<{ name: string; link: string }>;
}

export type ScopeResult = 'good' | 'medium' | 'bad' | 'n/a' | 'n/d' | 'intermediate';

export interface IRule extends IBaseEntity {
  scopeKey: string;
  version: string;
  description: string;
  good: string | null;
  medium: string | null;
  bad: string | null;
}

export interface IHistory extends IBaseEntity {
  event: string;
  additionalInfo: string;
}

export type HistoryPaginated = Paginated<IHistory>;
export type ChasingRefPaginated = Paginated<IChasingRef>;
export type ResultPaginated = Paginated<IApplicationResult>;

export type HistoryEntity = string;
export type HistoryAction = 'creation' | 'deletion' | 'addition' | 'update';

export type HistoryEvent = `${HistoryEntity}.${HistoryAction}`;

export interface IRoadmapAncestors extends IRoadmap {
  parentRef?: IApplicationParentRef;
  scopeRef?: IApplicationScopeRef[];
}

export interface IRecordResult {
  good: number | null;
  bad: number | null;
  medium: number | null;
  noData: number | null;
  notApplicable: number | null;
  value: number | null;
  recordDate: string;
}

export interface IRecordResultsByKey {
  resultKey: string;
  results: IRecordResult[];
}

export interface ITotalCount {
  total: number;
}

export type ReducerActionMap<M extends Record<string, unknown>> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key;
      }
    : {
        type: Key;
        payload: M[Key];
      };
};

export type ComponentRefFromApi = {
  componentRefId: number;
  componentRefName: string;
  componentRefType: string | null;
  scopeRefId: number;
  scopeRefName: string;
};

export interface RecommandationWithRemediation {
  applicationId: number;
  applicationName: string;
  applicationTribe: string;
  applicationCriticality: string;
  applicationStatus: string;
  parentRefId: number;
  scopeRefId: number;
  scopeRefName: number;
  componentRefId: number | null;
  componentRefKey: string;
  componentRefName: string;
  componentRefType: string;
  versionRefId: number;
  version: string;
  recommendationId: string;
  recommendationCreatedDate: string;
  isManagedByExtendedTransactis: boolean;
  recommendationTitle: string;
  recommendationComment: string;
  recommendationDocumentationLink: string | null;
  recommendationActualValue: string | null;
  recommendationExpectedValue: string | null;
  recommendationStatus: RecommendationStatus;
  recommendationCriticity: string;
  recommendationType: string;
  roadmapExpectedDate: string | null;
  recommendationDueDate: string | null;
  roadmapId: number | null;
  roadmapComment: string | null;
  roadmapStatus: string | null;
  roadmapType: string | null;
  roadmapAssets: AssetDTO[] | null;
  roadmapAssetNames: Array<string>;
  roadmapAssetZones: Array<string>;
  exceptionComment: string | null;
  reportingSort: string | null;
  exceptionRafId: string | null;
  exceptionAssets: string | null;
  exceptionAssetNames: string | null;
  exceptionAssetZones: string | null;
  resultType: string | null;
  noMainResultValues: { name: string | null; value: string | null }[] | null;
  kearID: string;
  ownersEmail: string[];
}

export interface Inventory {
  applicationId: number;
  kearID: string;
  resultId: number;
  resultValue: string;
  applicationTribe: string | null;
  iAppliName: string;
  applicationTrigram: string | null;
  applicationCriticality: string | null;
  applicationType: string | null;
  applicationOwnershipRc: string | null;
  componentRefId: number | null;
  componentRefKey: string | null;
  componentRefType: string | null;
  componentRefName: string | null;
  versionRefId: number | null;
  version: string | null;
  assetServerCloudType: string | null;
  assetNumberOfCores: string | null;
  assetInstalledMemory: string | null;
  assetNumberOfLogicalCpu: string | null;
  assetServerStatus: string | null;
  assetServerEnvironment: string | null;
  assetServerServiceOffer: string | null;
  assetHostname: string | null;
  assetZone: string | null;
  assetName: string | null;
  assetUid: string | null;
  eolActive: string | null;
  eolSecurity: string | null;
  eolExtended: string | null;
  eolSupport: string | null;
  lifeCycleManagement: string | null;
  eolSupportDateSource: string | null;
  eolSupportDateLastUpdateBy: string | null;
  eolSupportDateLastUpdateDate: string | null;
  exceptionComment: string | null;
  roadmapId: number | null;
  roadmapExpectedDate: string | null;
  roadmapComment: string | null;
  itrmObsolescenceStatus: string;
  recommendationStatus: string | null;
  recommendationTitle: string | null;
  recommendationId: number | null;
  reportingSort: string | null;
  isManagedByExtendedTransactis: boolean;
  ownersEmail: string[];
  assetTransformationPath: string | null;
  assetTransformationTarget: string | null;
  applicationTransformationPath: string | null;
  applicationTransformationTarget: string | null;
  applicationAustinStartDate: string | null;
  applicationAustinEndDate: string | null;
}

export type GroupedFilters = Array<Record<string, string | null>>;
export type FilterPossibleValues = GroupedFilters | string[];

export interface RecommandationWithRemediationFiltersValues {
  filters: Record<keyof RecommandationWithRemediation | 'scopes', FilterPossibleValues>;
}

export interface InventoryFiltersValues {
  filters: Record<keyof Inventory, FilterPossibleValues>;
}

export enum WebAppEnvironment {
  PRODUCTION = 'production',
  DEVELOPMENT = 'development',
}

export interface DrawGraphNodeData {
  id?: string;
  label?: string;
  color?: string;
  source?: string;
  target?: string;
  arrow?: string;
  type?: string;
  key?: string;
  countLink?: number;
  links?: string;
  level?: number;
}

export interface WebAppNode {
  data: DrawGraphNodeData;
  position?: string;
}

export * from './bookmark.type';
export * from './communications.type';
export * from './exception.type';
export * from './filter.type';
export * from './nova.type';
export * from './obsolescence.type';
export * from './recommendation-next.type';
export * from './remediations.type';
export * from './roadmap.type';
export * from './thirdpartySoftware.type';
export * from './total.type';
export * from './user.type';
export * from './versionRef.type';
export * from './persistent-result.type';
export * from './kpi.type';
export * from './excel.type';
